/**
 * Servicio de Clientes que usa la nueva API backend
 * Siguiendo el mismo patrón que authApiService.js
 */

import { apiHelpers } from './normalizedApiClient';
import { API_ENDPOINTS } from '../config/api';
import { clienteTransforms } from '../utils/dataTransforms';
import { normalizeDataArray, normalizeDataObject } from '../utils/normalizationUtils';

/**
 * Obtiene todos los clientes
 * @returns {Promise<Array>} - Lista de clientes
 */
export const getClientes = async () => {
  try {
    //console.log('👥 [ClientesService] Obteniendo todos los clientes');
    
    // Usar cliente normalizado que automáticamente normaliza respuestas
    const response = await apiHelpers.clientes.get(API_ENDPOINTS.CLIENTES.BASE);
    
    if (response.data) {
      // Extraer datos de la respuesta (ya vienen normalizados)
      const clientesData = response.data.data || response.data.clientes || response.data;
      //console.log(`✅ [ClientesService] ${clientesData.length} clientes encontrados (ya normalizados)`);
      
      // Aplicar transformaciones específicas adicionales si es necesario
      const clientesTransformados = clienteTransforms.fromApiList(clientesData);
      
      return clientesTransformados;
    }
    
    return [];
  } catch (error) {
    console.error('❌ [ClientesService] Error al obtener clientes:', error);
    throw error;
  }
};

/**
 * Crea un nuevo cliente
 * @param {Object} cliente - Datos del cliente a crear
 * @returns {Promise<Object>} - Cliente creado
 */
export const createCliente = async (cliente) => {
  try {
    //console.log('👥 [ClientesService] Creando nuevo cliente');
    
    // Transformar datos para envío a API
    const clienteData = clienteTransforms.toApi(cliente);
    
    // Usar cliente normalizado
    const response = await apiHelpers.clientes.post(API_ENDPOINTS.CLIENTES.BASE, clienteData);
    
    if (response.data) {
      // Extraer datos del cliente creado (ya normalizado)
      const clienteCreado = response.data.data || response.data.cliente || response.data;
      //console.log('✅ [ClientesService] Cliente creado exitosamente (ya normalizado)');
      
      // Transformar respuesta para compatibilidad
      return clienteTransforms.fromApi(clienteCreado);
    }
    
    throw new Error('No se recibió respuesta válida del servidor');
  } catch (error) {
    console.error('❌ [ClientesService] Error al crear cliente:', error);
    throw error;
  }
};

/**
 * Actualiza un cliente existente
 * @param {string|number} id - ID del cliente a actualizar
 * @param {Object} cliente - Datos actualizados del cliente
 * @returns {Promise<Object>} - Cliente actualizado
 */
export const updateCliente = async (id, cliente) => {
  try {

    
    // Transformar datos para envío a API
    const clienteData = clienteTransforms.toApi(cliente);
    
    const response = await apiHelpers.clientes.put(API_ENDPOINTS.CLIENTES.BY_ID(id), clienteData);
    
    if (response.data) {

      
      // Extraer datos del cliente actualizado
      const clienteActualizado = response.data.data || response.data.cliente || response.data;
      
      // Transformar respuesta para compatibilidad
      return clienteTransforms.fromApi(clienteActualizado);
    }
    
    throw new Error('No se recibió respuesta válida del servidor');
  } catch (error) {
    console.error(`❌ [ClientesService] Error al actualizar cliente ${id}:`, error);
    throw error;
  }
};

/**
 * Elimina un cliente (soft delete)
 * @param {string|number} id - ID del cliente a eliminar
 * @returns {Promise<string>} - ID del cliente eliminado
 */
export const deleteCliente = async (id) => {
  try {
    //console.log(`🗑️ [ClientesService] Eliminando cliente ${id}...`);
    
    const response = await apiHelpers.clientes.delete(API_ENDPOINTS.CLIENTES.BY_ID(id));
    
    if (response.data) {
      //console.log(`✅ [ClientesService] Cliente ${id} eliminado exitosamente`);
      return id.toString();
    }
    
    throw new Error('No se recibió respuesta válida del servidor');
  } catch (error) {
    console.error(`❌ [ClientesService] Error al eliminar cliente ${id}:`, error);
    throw error;
  }
};

/**
 * Obtiene un cliente por su ID
 * @param {string|number} id - ID del cliente
 * @returns {Promise<Object|null>} - Cliente encontrado o null
 */
export const getClienteById = async (id) => {
  try {
    //console.log(`🔍 [ClientesService] Buscando cliente por ID: ${id}`);
    
    if (!id) {
      return null;
    }
    
    const response = await apiHelpers.clientes.get(API_ENDPOINTS.CLIENTES.BY_ID(id));
    
    if (response.data) {
      //console.log('✅ [ClientesService] Cliente encontrado por ID:', response.data);
      
      // Extraer datos del cliente
      const clienteData = response.data.data || response.data.cliente || response.data;
      
      // Transformar respuesta para compatibilidad
      return clienteTransforms.fromApi(clienteData);
    }
    
    return null;
  } catch (error) {
    if (error.response?.status === 404) {
      //console.log(`ℹ️ [ClientesService] Cliente con ID ${id} no encontrado`);
      return null;
    }
    
    console.error(`❌ [ClientesService] Error al buscar cliente por ID ${id}:`, error);
    throw error;
  }
};

/**
 * Obtiene un cliente por su email
 * @param {string} email - Email del cliente
 * @returns {Promise<Object|null>} - Cliente encontrado o null
 */
export const getClienteByEmail = async (email) => {
  try {
    //console.log(`🔍 [ClientesService] Buscando cliente por email: ${email}`);
    
    if (!email) {
      return null;
    }
    
    const response = await apiHelpers.clientes.get(API_ENDPOINTS.CLIENTES.BY_EMAIL(email));
    
    if (response.data) {
      //console.log('✅ [ClientesService] Cliente encontrado por email:', response.data);
      
      // Extraer datos del cliente
      const clienteData = response.data.data || response.data.cliente || response.data;
      
      // Transformar respuesta para compatibilidad
      return clienteTransforms.fromApi(clienteData);
    }
    
    return null;
  } catch (error) {
    if (error.response?.status === 404) {
      //console.log(`ℹ️ [ClientesService] Cliente con email ${email} no encontrado`);
      return null;
    }
    
    console.error(`❌ [ClientesService] Error al buscar cliente por email ${email}:`, error);
    throw error;
  }
};

/**
 * Obtiene un cliente por su RUT
 * @param {string} rut - RUT del cliente
 * @returns {Promise<Object|null>} - Cliente encontrado o null
 */
export const getClienteByRut = async (rut) => {
  try {
    //console.log(`🔍 [ClientesService] Buscando cliente por RUT: ${rut}`);
    
    if (!rut) {
      return null;
    }
    
    const response = await apiHelpers.clientes.get(API_ENDPOINTS.CLIENTES.BY_RUT(rut));
    
    if (response.data) {
      //console.log('✅ [ClientesService] Cliente encontrado por RUT:', response.data);
      
      // Extraer datos del cliente
      const clienteData = response.data.data || response.data.cliente || response.data;
      
      // Transformar respuesta para compatibilidad
      return clienteTransforms.fromApi(clienteData);
    }
    
    return null;
  } catch (error) {
    if (error.response?.status === 404) {
      //console.log(`ℹ️ [ClientesService] Cliente con RUT ${rut} no encontrado`);
      return null;
    }
    
    console.error(`❌ [ClientesService] Error al buscar cliente por RUT ${rut}:`, error);
    throw error;
  }
};

/**
 * Obtiene un cliente por su Clerk ID
 * @param {string} clerkId - Clerk ID del cliente
 * @returns {Promise<Object|null>} - Cliente encontrado o null
 */
export const getClienteByClerkId = async (clerkId) => {
  try {
    //console.log(`🔍 [ClientesService] Buscando cliente por Clerk ID: ${clerkId}`);
    
    if (!clerkId) {
      return null;
    }
    
    const response = await apiHelpers.clientes.get(API_ENDPOINTS.CLIENTES.BY_CLERK_ID(clerkId));
    
    if (response.data) {
      //console.log('✅ [ClientesService] Cliente encontrado por Clerk ID:', response.data);
      
      // Extraer datos del cliente
      const clienteData = response.data.data || response.data.cliente || response.data;
      
      // Transformar respuesta para compatibilidad
      return clienteTransforms.fromApi(clienteData);
    }
    
    return null;
  } catch (error) {
    if (error.response?.status === 404) {
      //console.log(`ℹ️ [ClientesService] Cliente con Clerk ID ${clerkId} no encontrado`);
      return null;
    }
    
    console.error(`❌ [ClientesService] Error al buscar cliente por Clerk ID ${clerkId}:`, error);
    throw error;
  }
};

/**
 * Busca clientes por término de búsqueda
 * @param {string} termino - Término de búsqueda
 * @param {number} empresaId - ID de la empresa (opcional para compatibilidad)
 * @returns {Promise<Array>} - Lista de clientes encontrados
 */
export const buscarClientes = async (termino, empresaId = null) => {
  try {
    //console.log(`🔍 [ClientesService] Buscando clientes con término: ${termino}`);
    
    if (!termino || termino.trim().length === 0) {
      return [];
    }
    
    const response = await apiHelpers.clientes.get(API_ENDPOINTS.CLIENTES.BUSCAR, {
      params: { q: termino.trim() }
    });
    
    if (response.data) {
      //console.log('✅ [ClientesService] Búsqueda de clientes exitosa:', response.data);
      
      // Extraer datos de la respuesta
      const clientesData = response.data.data || response.data.clientes || response.data;
      
      // Transformar datos para compatibilidad con frontend
      const clientesTransformados = clienteTransforms.fromApiList(clientesData);
      
      return clientesTransformados;
    }
    
    return [];
  } catch (error) {
    console.error(`❌ [ClientesService] Error al buscar clientes con término ${termino}:`, error);
    throw error;
  }
};

/**
 * Obtiene estadísticas de clientes para una empresa
 * @param {number} empresaId - ID de la empresa (opcional para compatibilidad)
 * @returns {Promise<Object>} - Estadísticas de clientes
 */
export const getClientesStats = async (empresaId = null) => {
  try {
    //console.log('📊 [ClientesService] Obteniendo estadísticas de clientes...');
    
    const response = await apiHelpers.clientes.get(API_ENDPOINTS.CLIENTES.STATS || `${API_ENDPOINTS.CLIENTES.BASE}/stats`);
    
    if (response.data) {
      const stats = response.data.data || response.data;
      return {
        totalActivos: stats.totalActivos || stats.total || 0,
        nuevosEsteMes: stats.nuevosEsteMes || stats.nuevos || 0
      };
    }
    
    return {
      totalActivos: 0,
      nuevosEsteMes: 0
    };
  } catch (error) {
    console.error('❌ [ClientesService] Error al obtener estadísticas:', error);
    // Fallback: retornar valores por defecto en caso de error
    return {
      totalActivos: 0,
      nuevosEsteMes: 0
    };
  }
};

/**
 * Alias para compatibilidad con la API multi-empresa
 * @param {string} termino - Término de búsqueda
 * @param {number} empresaId - ID de la empresa
 * @returns {Promise<Array>} - Lista de clientes encontrados
 */
export const searchClientes = buscarClientes;

/**
 * Hook personalizado para usar el servicio de clientes
 */
export const useClientesApiService = () => {
  return {
    getClientes,
    createCliente,
    updateCliente,
    deleteCliente,
    getClienteById,
    getClienteByEmail,
    getClienteByRut,
    getClienteByClerkId,
    buscarClientes,
    searchClientes,
    getClientesStats
  };
};

// Las funciones ya están exportadas individualmente arriba